import {
  BLACK_HEX,
  DEFAULT_COLOR,
  DEFAULT_LINE_WIDTH,
  DEFAULT_OPACITY
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { EChartsType, SeriesOption } from "echarts";
import { curry } from "lodash";
import debounce from "lodash/debounce";
import { ChartActionType, SeriesType } from "types/echarts";
import { convertQuarterToMonth } from "utils/convertQuarterToMonth";

/**
 * Accepts a chart instance, an action type, and then a series index.
 */
export const dispatchAction = curry((chartInstance, actionType, seriesIndex: number) => {
  chartInstance.dispatchAction({
    type: actionType,
    seriesIndex
  });
});

/**
 * Resets scatter highlights by hiding the target graphic.
 */
export function _resetScatterHighlight(chartInstance) {
  chartInstance.setOption({
    graphic: {
      id: "target",
      type: "circle",
      invisible: true
    }
  });
}
export const resetScatterHighlight = debounce(_resetScatterHighlight, 100);
/**
 *reset highlights for pie chart
 * @param {Array} selectedGroups - The array of selected groups.
 * @param {Object} chartInstanceRef - The reference to the chart instance.
 */
export function resetPieSeriesHighlights(chartInstance, selectedGroups) {
  chartInstance.dispatchAction({
    type: ChartActionType.Downplay
  });
  chartInstance.dispatchAction({
    type: ChartActionType.Highlight,
    name: selectedGroups
  });
}
/**
 * Allows highlighting between the focus and map for pie chart
 *
 * @param {Array} seriesArray - The array of series data.
 * @param {string} hoverLegendItem - The name of the hovered legend item.
 * @param {string} hoverLegendGroup - The name of the hovered legend group.
 * @param {Array} selectedGroups - The array of selected groups.
 * @param {Object} chartInstance - The reference to the chart instance.
 */
export function updatePieSeries(
  seriesArray,
  hoverLegendItem,
  selectedGroups,
  chartInstance
) {
  const itemsToHighlight = [...selectedGroups, hoverLegendItem];

  const itemsToDownplay = seriesArray[0].data
    .map((item) => item.name)
    .filter((item) => !itemsToHighlight.includes(item));

  if (itemsToHighlight.length > 0) {
    chartInstance.dispatchAction({
      type: ChartActionType.Highlight,
      name: itemsToHighlight
    });
  }
  // Dispatch downplay action for non-selected/non-hovered items
  if (itemsToDownplay.length > 0) {
    chartInstance.dispatchAction({
      type: ChartActionType.Downplay,
      name: itemsToDownplay
    });
  }
}
/**
 * Updates the emphasis of the target data point and dispatches a highlight action
 *
 * @param {EChartsType} chartInstance - chart instance to update
 * @param {number} seriesIndex - index of the target series
 * @param {number} dataIndex - index of the data point to isolate
 * @param {number} pointSizeValue - size of the point to be displayed
 *
 */
export function _updateTargetDataEmphasis(
  chartInstance,
  seriesIndex,
  dataIndex,
  pointSizeValue,
  chartType
) {
  if (!chartInstance) return;
  const isMaterialBalanceChart = chartType === EvaChart.MaterialBalanceTime;

  const chartOptions = chartInstance.getOption();
  if (!chartOptions?.series) return;

  const seriesArray = chartOptions.series;
  const targetSeries = seriesArray[seriesIndex];

  if (targetSeries?.type !== SeriesType.Scatter) return;
  if (!targetSeries?.data) return;
  // only guard against undefined, data point can be 0
  if (targetSeries.data[dataIndex] === undefined) return;
  let x, y;
  if (targetSeries.dimensions?.length) {
    const dimensions = targetSeries.dimensions.length;
    x = targetSeries.data[dimensions * dataIndex];
    y = targetSeries.data[dimensions * dataIndex + 1];
  } else {
    x = targetSeries.data[dataIndex][0];
    y = targetSeries.data[dataIndex][1];
  }

  chartInstance.setOption({
    graphic: {
      id: "target",
      invisible: false,
      type: "circle",
      silent: true,
      position: chartInstance.convertToPixel("grid", [x, y]),
      shape: { cx: 0, cy: 0, r: pointSizeValue / 2 }, // pointSizeValue is diameter, needs /2 for radius
      z: 100,
      // when highlighting forecasts on the material balance chart,
      // only the border is highlighted
      ...(isMaterialBalanceChart && targetSeries?.itemStyle?.color === "none"
        ? {
            style: {
              fill: "none",
              stroke: DEFAULT_COLOR,
              lineWidth: DEFAULT_LINE_WIDTH
            }
          }
        : {})
    }
  });
}
export const updateTargetDataEmphasis = debounce(_updateTargetDataEmphasis, 100);

export function convertMonthToQuarter(xDateString: string): string {
  if (!xDateString.includes("-")) {
    return xDateString;
  }

  const [year, month] = xDateString.split("-").map(Number);
  if (!year || !month) {
    return xDateString;
  }

  const quarter = Math.floor((month - 1) / 3) + 1;
  return `${year}-Q${quarter}`;
}
/*
 * Updates the emphasis of the target based on x, y
 *
 * @param {EChartsType} chartInstance - chart instance to update
 * @param {number} x - x coordinate of the data point to isolate
 * @param {number} y - y coordinate of the data point to isolate
 * @param {number} pointSizeValue - size of the point to be displayed
 * @param {boolean} isForecast - whether the data point is a forecast
 *
 */
export function _updateDataPointEmphasis({
  chartInstance,
  x,
  y,
  pointSizeValue,
  isForecast
}: {
  chartInstance: EChartsType;
  x: number;
  y: number;
  pointSizeValue: number;
  isForecast?: boolean;
}) {
  if (!chartInstance) return;
  const xCord = typeof x === "string" ? convertQuarterToMonth(x) : x;

  chartInstance.setOption({
    graphic: {
      id: "target",
      invisible: false,
      type: "circle",
      silent: true,
      position: chartInstance.convertToPixel("grid", [xCord, y]),
      shape: { cx: 0, cy: 0, r: pointSizeValue / 2 }, // pointSizeValue is diameter, needs /2 for radius
      z: 100,
      // when highlighting forecasts on the scatter charts,
      // only the border is highlighted
      ...(isForecast
        ? {
            style: {
              fill: "none",
              stroke: DEFAULT_COLOR,
              lineWidth: DEFAULT_LINE_WIDTH
            }
          }
        : {
            style: {
              fill: DEFAULT_COLOR
            }
          })
    }
  });
}

export const updateDataPointEmphasis = debounce(_updateDataPointEmphasis, 100);

/**
 * Updates the emphasis of the target based on x, y for material balance chart
 *
 * @param {EChartsType} chartInstance - chart instance to update
 * @param {number} x - x coordinate of the data point to isolate
 * @param {number} y - y coordinate of the data point to isolate
 * @param {string} label - label of the data point to isolate
 * @param {number} pointSizeValue - size of the point to be displayed
 *
 */
export function _updateMaterialBalanceDataPointEmphasis({
  chartInstance,
  x,
  y,
  label,
  pointSizeValue
}: {
  chartInstance: EChartsType;
  x: number;
  y: number;
  label: string;
  pointSizeValue: number;
}) {
  if (!chartInstance) return;

  const isForecast = label.includes("Forecast");

  chartInstance.setOption({
    graphic: {
      id: "target",
      invisible: false,
      type: "circle",
      silent: true,
      position: chartInstance.convertToPixel("grid", [x, y]),
      shape: { cx: 0, cy: 0, r: pointSizeValue / 2 }, // pointSizeValue is diameter, needs /2 for radius
      z: 100,
      // when highlighting forecasts on the material balance chart,
      // only the border is highlighted
      ...(isForecast
        ? {
            style: {
              fill: "none",
              stroke: DEFAULT_COLOR,
              lineWidth: DEFAULT_LINE_WIDTH
            }
          }
        : {})
    }
  });
}
export const updateMaterialBalanceDataPointEmphasis = debounce(
  _updateMaterialBalanceDataPointEmphasis,
  100
);

export function highlightWellByUpdatingJitterStyle(
  seriesArray: SeriesOption[],
  seriesIndex: number,
  dataIndex: number
): SeriesOption[] {
  // 1) Clone every series, but only copy `.data` if it's an array
  const cloned = seriesArray.map((s) => {
    const copy: Partial<SeriesOption> = { ...s };

    if (Array.isArray(s.data)) {
      copy.data = s.data.map((row) => (Array.isArray(row) ? [...row] : { ...row }));
    } else {
      // no data array, so remove it entirely
      delete copy.data;
    }

    return copy as SeriesOption;
  });

  // 2) Now tweak the one point
  const target = cloned[seriesIndex];
  if (!target || !Array.isArray(target.data)) {
    return cloned;
  }

  const orig = target.data[dataIndex];
  if (!Array.isArray(orig)) {
    return cloned;
  }

  const newRow = [...orig];
  newRow[2] = BLACK_HEX;
  newRow[5] = DEFAULT_OPACITY;
  target.data[dataIndex] = newRow;

  return cloned;
}

const lineCharts = new Set<EvaChart>([
  EvaChart.CAGR,
  EvaChart.CumTime,
  EvaChart.DeclineRate,
  EvaChart.RateCum,
  EvaChart.RateTime,
  EvaChart.RateDate,
  EvaChart.TotalRateDate,
  EvaChart.TotalRateCum,
  EvaChart.RateDateMidstream,
  EvaChart.TotalRateDateMidstream,
  EvaChart.WellContribution
]);

export const isLineChart = (chartType: EvaChart): boolean => {
  return lineCharts.has(chartType);
};

export const isChartClickEnabled = (chartType: EvaChart): boolean => {
  return (
    chartType !== EvaChart.BoxPlot &&
    chartType !== EvaChart.BoxPlotMidstream &&
    chartType !== EvaChart.StackedBar &&
    chartType !== EvaChart.StackedBarHorizontal &&
    chartType !== EvaChart.CrossPlot &&
    chartType !== EvaChart.CrossPlotMidstream &&
    chartType !== EvaChart.Probit &&
    chartType !== EvaChart.MaterialBalanceTime
  );
};
